import { useQuery } from '@tanstack/react-query';
import { api } from './client';
import { useScopedSelect } from '@/contexts/ClientScopeContext';

/** Docs module (IT Glue) — mirrors server/docs/. Read-only; passwords never reach the browser. */

export interface DocsOrg {
  id: string;
  name: string;
  shortName: string | null;
  type: string | null;
  status: string | null;
  alert: string | null;
  quickNotesPreview: string | null;
  autotaskCompanyId: number | null;
  companyName: string | null;
  psa: 'enabled' | 'orphaned' | 'none';
  url: string | null;
  updatedAt: string | null;
  configurations: number;
  expiringSoon: number;
}

export interface RmmLink { uid: string; hostname: string; online: boolean; lastSeen: string | null }

export interface DocsConfig {
  id: string;
  orgId: string;
  orgName: string;
  name: string;
  type: string | null;
  status: string | null;
  archived: boolean;
  serial: string | null;
  manufacturer: string | null;
  model: string | null;
  os: string | null;
  ip: string | null;
  location: string | null;
  contact: string | null;
  warrantyExpires: string | null;
  url: string | null;
  updatedAt: string | null;
  autotaskCiId: number | null;
  dattoDeviceId: number | null;
  rmmLastSeen: string | null;
  hasIntune: boolean;
  /** On the all-configurations list only. */
  companyId?: number | null;
  companyName?: string | null;
  /** The live Datto RMM device for `dattoDeviceId` — null when IT Glue points at a device Datto no longer has. */
  rmm?: RmmLink | null;
}

export interface DocsExpiration { id: string; orgId: string; orgName: string; kind: string; name: string; description: string | null; date: string | null; url: string | null; companyId: number | null; companyName: string | null }

export interface DocsDocument { id: string; name: string; folderId: string | null; folder: string | null; uploaded: boolean; updatedAt: string | null; url: string | null }
export interface DocsContact { id: string; name: string; title: string | null; type: string | null; important: boolean; emails: string[]; autotaskContactId: number | null; url: string | null }

export interface DocsOrgDetail {
  org: DocsOrg & { quickNotes: string | null; description: string | null };
  documents: DocsDocument[];
  contacts: DocsContact[];
  domains: { id: string; name: string; registrar: string | null; expiresOn: string | null; url: string | null }[];
  ssl: { id: string; host: string; issuer: string | null; validUntil: string | null; url: string | null }[];
  configurations: DocsConfig[];
  expirations: Omit<DocsExpiration, 'companyId' | 'companyName'>[];
}

export interface DocsDocumentBody {
  id: string; name: string; orgId: string; url: string | null; updatedAt: string | null; uploaded: boolean;
  /** Section HTML — sanitise with DOMPurify before rendering. Image URLs expire after 1 h. */
  html: string;
  attachments: { name: string; size: number | null; url: string | null }[];
}

/** A normalised IT Glue flexible-asset field (server/docs/flexibleAssets.ts) — password fields never included. */
export interface FaField { label: string; kind: 'header' | 'text' | 'html' | 'tags' | 'bool'; value: string | null; tags?: { name: string; url: string | null }[] }
/** A flexible-asset record: `badge` is the type's badge field (e.g. Site Summary relationship status). */
export interface FaAsset { id: string; orgId: string; name: string; badge: string | null; updatedAt: string | null; url: string | null; fields: FaField[] }

/** IT Glue "SOP On/Offboarding" record (server/docs/sop.ts). */
export type SopField = FaField;
export interface SopAsset {
  id: string; orgId: string; name: string; kind: 'onboarding' | 'offboarding' | 'other'; typeLabel: string | null;
  updatedAt: string | null; url: string | null; fields: SopField[];
}

export interface DocsTicketContext {
  org: { id: string; name: string; alert: string | null; quickNotes: string | null; url: string | null } | null;
  configuration?: DocsConfig | null;
  /** Only documents relevant to the ticket title (empty when none match). */
  documents?: { id: string; name: string; updatedAt: string | null; url: string | null }[];
  documentCount?: number;
  /** Set when the ticket title reads like a new starter / leaver; `items` empty = no SOP documented. */
  sop?: { kind: 'onboarding' | 'offboarding'; items: SopAsset[] } | null;
}

const STALE = 5 * 60_000;

/** `scoped`: narrow to the client picked in the header (ClientScopeContext). */
export function useDocsOrgs(opts: { scoped?: boolean } = {}) {
  const select = useScopedSelect<{ items: DocsOrg[]; fetchedAt: string }>(opts.scoped, (d, inScope) => ({ ...d, items: d.items.filter((x) => inScope(x.autotaskCompanyId)) }));
  return useQuery({ select, queryKey: ['docs', 'orgs'], queryFn: () => api.get<{ items: DocsOrg[]; fetchedAt: string }>('/api/docs/orgs'), staleTime: STALE });
}

export function useDocsOrg(id: string | undefined) {
  return useQuery({ queryKey: ['docs', 'org', id], queryFn: () => api.get<DocsOrgDetail>(`/api/docs/orgs/${id}`), enabled: !!id, staleTime: STALE });
}

/** `scoped`: narrow to the client picked in the header (ClientScopeContext). */
export function useDocsConfigs(opts: { scoped?: boolean } = {}) {
  const select = useScopedSelect<{ items: DocsConfig[]; fetchedAt: string }>(opts.scoped, (d, inScope) => ({ ...d, items: d.items.filter((x) => inScope(x.companyId)) }));
  return useQuery({ select, queryKey: ['docs', 'configs'], queryFn: () => api.get<{ items: DocsConfig[]; fetchedAt: string }>('/api/docs/configurations'), staleTime: STALE });
}

/** `scoped`: narrow to the client picked in the header (ClientScopeContext). */
export function useDocsExpirations(opts: { scoped?: boolean } = {}) {
  const select = useScopedSelect<{ items: DocsExpiration[]; fetchedAt: string }>(opts.scoped, (d, inScope) => ({ ...d, items: d.items.filter((x) => inScope(x.companyId)) }));
  return useQuery({ select, queryKey: ['docs', 'expirations'], queryFn: () => api.get<{ items: DocsExpiration[]; fetchedAt: string }>('/api/docs/expirations'), staleTime: STALE });
}

/** Document bodies carry pre-signed image URLs valid for 1 h — keep them well inside that. */
export function useDocsDocument(id: string | null) {
  return useQuery({ queryKey: ['docs', 'document', id], queryFn: () => api.get<DocsDocumentBody>(`/api/docs/documents/${id}`), enabled: !!id, staleTime: 10 * 60_000, gcTime: 20 * 60_000 });
}

export function useDocsForDevice(uid: string | undefined) {
  return useQuery({ queryKey: ['docs', 'rmm', uid], queryFn: () => api.get<{ items: DocsConfig[] }>(`/api/docs/rmm/${uid}`), enabled: !!uid, staleTime: STALE, retry: false });
}

/** The org's "Site Summary" records (top of the org page). */
export function useDocsOrgSiteSummary(orgId: string | undefined) {
  return useQuery({ queryKey: ['docs', 'org', orgId, 'site-summary'], queryFn: () => api.get<{ items: FaAsset[] }>(`/api/docs/orgs/${orgId}/site-summary`), enabled: !!orgId, staleTime: STALE });
}

/** The org's SOP On/Offboarding records (org page tab). */
export function useDocsOrgSop(orgId: string | undefined) {
  return useQuery({ queryKey: ['docs', 'org', orgId, 'sop'], queryFn: () => api.get<{ items: SopAsset[] }>(`/api/docs/orgs/${orgId}/sop`), enabled: !!orgId, staleTime: STALE });
}

export function useDocsTicketContext(p: { companyId: number | null | undefined; ciId?: number | null; title?: string }) {
  const qs = new URLSearchParams();
  if (p.companyId != null) qs.set('companyId', String(p.companyId));
  if (p.ciId) qs.set('ciId', String(p.ciId));
  if (p.title) qs.set('title', p.title.slice(0, 300));
  return useQuery({
    queryKey: ['docs', 'context', p.companyId, p.ciId ?? null, p.title ?? ''],
    queryFn: () => api.get<DocsTicketContext>(`/api/docs/context?${qs}`),
    enabled: p.companyId != null,
    staleTime: STALE,
    retry: false,
  });
}
